import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Description as DocumentIcon,
  Assignment as AssignmentIcon,
  VerifiedUser as VerifiedIcon,
  Work as WorkIcon,
  Home as HomeIcon,
  AccountBalance as BankIcon,
  FamilyRestroom as FamilyIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config/api';

// Import components
import EmployeeSelector from '../components/admin/OnboardingManagement/EmployeeSelector';
import ITOnboardingForm from '../components/admin/OnboardingManagement/ITOnboardingForm';
import WelcomePreparation from '../components/admin/OnboardingManagement/WelcomePreparation';
import ITAssetsHandover from '../components/admin/OnboardingManagement/ITAssetsHandover';
import BrandingAssets from '../components/admin/OnboardingManagement/BrandingAssets';
import LoginCredentials from '../components/admin/OnboardingManagement/LoginCredentials';
import OnboardingStep from '../components/admin/OnboardingManagement/OnboardingStep';
import EmployeeUpload from '../components/admin/OnboardingManagement/EmployeeUpload';
import OfficialDocuments from '../components/admin/OnboardingManagement/OfficialDocuments';
import DocumentVerification from '../components/admin/OnboardingManagement/DocumentVerification';

const AdminOnboardingManagement = () => {
  const { user, getAuthHeaders } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [employeeProgress, setEmployeeProgress] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check if user is IT Support (limited access)
  const isITSupport = user?.primaryRole === 'itsupport';
  const isAdmin = user?.primaryRole === 'admin';

  // Onboarding steps configuration
  const onboardingSteps = [
    {
      id: 1,
      title: 'IT On-boarding Form',
      description: 'Complete IT setup and system access requirements',
      department: 'HR & IT',
      icon: <ComputerIcon />,
      component: 'ITOnboardingForm',
      accessibleBy: ['admin', 'itsupport'],
    },
    {
      id: 2,
      title: 'Preparing for Welcome',
      description: 'Welcome kit and orientation preparation',
      department: 'HR & Marketing',
      icon: <PersonIcon />,
      component: 'WelcomePreparation',
      accessibleBy: ['admin', 'itsupport'],
    },
    {
      id: 3,
      title: 'IT Assets Handover',
      description: 'Laptop, phone, and other IT equipment allocation',
      department: 'Admin, IT & HR',
      icon: <ComputerIcon />,
      component: 'ITAssetsHandover',
      accessibleBy: ['admin', 'itsupport'],
    },
    {
      id: 4,
      title: 'Company Branding Assets',
      description: 'Company email, business cards, and branding materials',
      department: 'Admin & HR',
      icon: <BusinessIcon />,
      component: 'BrandingAssets',
      accessibleBy: ['admin', 'itsupport'],
    },
    {
      id: 5,
      title: 'Create Login Credentials',
      description: 'System access and account creation',
      department: 'Admin & IT',
      icon: <SecurityIcon />,
      component: 'LoginCredentials',
      accessibleBy: ['admin', 'itsupport'],
    },
    {
      id: 6,
      title: 'Onboarding Form',
      description: 'Employee personal and contact details',
      department: 'Employee',
      icon: <PersonIcon />,
      component: 'OnboardingForm',
      accessibleBy: ['admin'],
    },
    {
      id: 7,
      title: 'Employee Document Upload',
      description: 'Required documents and certificates',
      department: 'Employee',
      icon: <DocumentIcon />,
      component: 'EmployeeUpload',
      accessibleBy: ['admin'],
    },
    {
      id: 8,
      title: 'Official Document',
      description: 'Legal documents and compliance forms',
      department: 'HR & Employee',
      icon: <DocumentIcon />,
      component: 'OfficialDocuments',
      accessibleBy: ['admin'],
    },
    {
      id: 9,
      title: 'Document Verification',
      description: 'Final verification and approval process',
      department: 'HR',
      icon: <VerifiedIcon />,
      component: 'DocumentVerification',
      accessibleBy: ['admin'],
    },
  ];

  // Filter steps based on user role
  const getFilteredSteps = () => {
    if (isAdmin) {
      return onboardingSteps;
    } else if (isITSupport) {
      return onboardingSteps.slice(0, 5); // Only first 5 steps
    }
    return [];
  };

  const filteredSteps = getFilteredSteps();

  // Fetch employee progress when employee is selected
  useEffect(() => {
    if (selectedEmployee?.employeeId) {
      fetchEmployeeProgress();
    } else {
      setEmployeeProgress(null);
      setCompletedSteps(new Set());
    }
  }, [selectedEmployee]);

  const fetchEmployeeProgress = async () => {
    if (!selectedEmployee?.employeeId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin-onboarding-steps/progress/${selectedEmployee.employeeId}`,
        { headers: getAuthHeaders() }
      );
      
      const progress = response.data.data;
      setEmployeeProgress(progress);
      
      // Update completed steps based on backend data
      const completedStepsSet = new Set();
      progress.steps.forEach((step, index) => {
        if (step.completed) {
          completedStepsSet.add(index);
        }
      });
      setCompletedSteps(completedStepsSet);
      
      // Set active step to next incomplete step
      const nextIncompleteStep = progress.steps.findIndex(step => !step.completed);
      if (nextIncompleteStep !== -1) {
        setActiveStep(nextIncompleteStep);
      }
    } catch (error) {
      console.error('Error fetching employee progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStepClick = (stepIndex) => {
    // Only allow clicking on completed steps or the next available step
    if (stepIndex <= activeStep || completedSteps.has(stepIndex)) {
      setActiveStep(stepIndex);
    }
  };

  const handleNext = () => {
    // Only allow moving to next step if current step is completed
    if (completedSteps.has(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };



  const handleStepComplete = async (stepIndex) => {
    if (!selectedEmployee?.employeeId) return;

    try {
      const response = await axios.put(
        `${API_URL}/admin-onboarding-steps/progress/${selectedEmployee.employeeId}/step/${stepIndex + 1}/complete`,
        { completedBy: user._id },
        { headers: getAuthHeaders() }
      );

      // Update local state
      setCompletedSteps(prev => new Set([...prev, stepIndex]));
      setEmployeeProgress(response.data.data);
      
      // Auto-advance to next step
      if (stepIndex < filteredSteps.length - 1) {
        setActiveStep(stepIndex + 1);
      }
    } catch (error) {
      console.error('Error marking step complete:', error);
    }
  };

  const handleStepIncomplete = async (stepIndex) => {
    if (!selectedEmployee?.employeeId) return;

    try {
      const response = await axios.put(
        `${API_URL}/admin-onboarding-steps/progress/${selectedEmployee.employeeId}/step/${stepIndex + 1}/incomplete`,
        {},
        { headers: getAuthHeaders() }
      );

      // Update local state
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(stepIndex);
        return newSet;
      });
      setEmployeeProgress(response.data.data);
    } catch (error) {
      console.error('Error marking step incomplete:', error);
    }
  };

  const handleEmployeeChange = (employeeData) => {
    setSelectedEmployee(employeeData);
    setActiveStep(0);
  };

  // Render component based on step
  const renderStepComponent = (step, stepIndex) => {
    const commonProps = {
      selectedEmployee,
      onStepComplete: () => handleStepComplete(stepIndex),
      onStepIncomplete: () => handleStepIncomplete(stepIndex),
      isCompleted: completedSteps.has(stepIndex),
      employeeProgress,
    };

    switch (step.component) {
      case 'ITOnboardingForm':
        return <ITOnboardingForm {...commonProps} />;
      case 'WelcomePreparation':
        return <WelcomePreparation {...commonProps} />;
      case 'ITAssetsHandover':
        return <ITAssetsHandover {...commonProps} />;
      case 'BrandingAssets':
        return <BrandingAssets {...commonProps} />;
      case 'LoginCredentials':
        return <LoginCredentials {...commonProps} />;
      case 'OnboardingForm':
        return <OnboardingStep {...commonProps} />;
      case 'EmployeeUpload':
        return <EmployeeUpload {...commonProps} />;
      case 'OfficialDocuments':
        return <OfficialDocuments {...commonProps} />;
      case 'DocumentVerification':
        return <DocumentVerification {...commonProps} />;
      default:
        return <DefaultComponent step={step} {...commonProps} />;
    }
  };

  if (!isAdmin && !isITSupport) {
    return (
      <Container maxWidth={false}>
        <Alert severity="error" sx={{ mt: 3 }}>
          You don't have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, fontSize: '30px' }}>
          Onboarding Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isITSupport ? 'IT Support: Manage IT-related onboarding processes' : 'Admin: Manage all onboarding processes'}
        </Typography>
        {isITSupport && (
          <Alert severity="info" sx={{ mt: 2 }}>
            IT Support access: You can only manage IT-related onboarding steps (1-5). Admin users can access all 10 steps.
          </Alert>
        )}
      </Box>

      {/* Employee Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <EmployeeSelector 
          selectedEmployee={selectedEmployee}
          onEmployeeChange={handleEmployeeChange}
        />
      </Paper>

      {/* Progress Summary */}
      {selectedEmployee && employeeProgress && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '20px' }}>
            Progress Summary
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              Completed: {employeeProgress.completedSteps} / {employeeProgress.totalSteps} steps
            </Typography>
            <Chip 
              label={`${Math.round((employeeProgress.completedSteps / employeeProgress.totalSteps) * 100)}%`}
              color={employeeProgress.completedSteps === employeeProgress.totalSteps ? "success" : "primary"}
              variant="outlined"
            />
          </Box>
          {/* Display both IDs */}
          {/* <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Employee ID:</strong> {selectedEmployee.employeeId}
            </Typography>
            <Typography variant="body2">
              <strong>User ID:</strong> {selectedEmployee.userId || 'Not Assigned'}
            </Typography>
          </Box> */}
        </Paper>
      )}

      {/* Onboarding Steps Stepper */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontSize: '20px' }}>
          Onboarding Process Steps
        </Typography>
        
        <Stepper orientation="vertical" activeStep={activeStep}>
          {filteredSteps.map((step, index) => (
            <Step key={step.id} active={index === activeStep} completed={completedSteps.has(index)}>
              <StepLabel
                StepIconComponent={() => (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: completedSteps.has(index) ? 'success.main' : 
                             index === activeStep ? 'primary.main' : 'grey.300',
                      color: completedSteps.has(index) || index === activeStep ? 'white' : 'grey.600',
                      cursor: (index <= activeStep || completedSteps.has(index)) ? 'pointer' : 'not-allowed',
                      '&:hover': {
                        bgcolor: (index <= activeStep || completedSteps.has(index)) ? 'primary.main' : 'grey.300',
                        color: (index <= activeStep || completedSteps.has(index)) ? 'white' : 'grey.600',
                      }
                    }}
                    onClick={() => handleStepClick(index)}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '20px' }}>
                    {step.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label={step.department} 
                      size="small" 
                      variant="outlined"
                    />
                    {completedSteps.has(index) && (
                      <Chip 
                        label="Completed" 
                        size="small" 
                        color="success"
                      />
                    )}
                  </Box>
                </Box>
              </StepLabel>
              <StepContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Responsible:</strong> {step.department}
                  </Typography>
                </Box>
                
                {/* Step Component */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {renderStepComponent(step, index)}
                </Box>

                <Box sx={{ mb: 2, mt: 3 }}>
                  <div>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mt: 1, mr: 1 }}
                      disabled={index === filteredSteps.length - 1 || !completedSteps.has(index)}
                    >
                      {index === filteredSteps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        

      </Paper>
      </Container>
    </Box>
  );
};

const DefaultComponent = ({ step, isCompleted, onStepComplete, onStepIncomplete }) => {
  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      onStepComplete();
    } else {
      onStepIncomplete();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: '20px' }}>{step.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px' }}>
        Component for {step.title} will be implemented here.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={isCompleted}
            onChange={handleCheckboxChange}
            color="primary"
          />
        }
        label="Mark as Complete"
      />
    </Box>
  );
};

export default AdminOnboardingManagement; 